import type { SessionStatus, SessionSummary } from '@diana/contracts';

import { primaryClient } from './crm-format.js';

export type ShootQuickFilter = 'All' | 'New' | 'Upcoming' | 'ToDo';
export type ShootStatusFilter = 'All' | SessionStatus;
export type ShootListFilters = {
  search?: string;
  status?: ShootStatusFilter;
  quick?: ShootQuickFilter;
};
export type ShootListGroups = {
  requests: SessionSummary[];
  discussion: SessionSummary[];
  upcoming: SessionSummary[];
  deliver: SessionSummary[];
  past: SessionSummary[];
};
export type ShootClientSummary = {
  primaryClient: SessionSummary['clients'][number];
  additionalClientCount: number;
};

export function filterShoots(
  shoots: readonly SessionSummary[],
  filters: ShootListFilters = {},
): SessionSummary[] {
  const { search = '', status = 'All', quick = 'All' } = filters;
  const query = search.trim().toLocaleLowerCase();
  const quickStatus: Record<Exclude<ShootQuickFilter, 'All'>, SessionStatus> = {
    New: 'NewRequest',
    Upcoming: 'Scheduled',
    ToDo: 'Editing',
  };

  return shoots.filter((shoot) => {
    if (status !== 'All' && shoot.status !== status) return false;
    if (quick !== 'All' && shoot.status !== quickStatus[quick]) return false;
    if (!query) return true;
    const client = primaryClient(shoot);
    return [
      client.name,
      client.email,
      client.phone,
      client.instagram,
      shoot.title,
      shoot.isGiftCard ? 'Gift card' : null,
      shoot.requestedPackageTitle,
      shoot.location,
    ].some((value) => value?.toLocaleLowerCase().includes(query));
  });
}

export function groupShoots(shoots: readonly SessionSummary[]): ShootListGroups {
  const groups: ShootListGroups = {
    requests: [],
    discussion: [],
    upcoming: [],
    deliver: [],
    past: [],
  };
  for (const shoot of shoots) {
    switch (shoot.status) {
      case 'NewRequest':
        groups.requests.push(shoot);
        break;
      case 'InDiscussion':
        groups.discussion.push(shoot);
        break;
      case 'Scheduled':
        groups.upcoming.push(shoot);
        break;
      case 'Editing':
        groups.deliver.push(shoot);
        break;
      case 'Delivered':
      case 'Canceled':
        groups.past.push(shoot);
        break;
    }
  }
  groups.upcoming.sort((a, b) => compareScheduledDates(a, b, 1));
  groups.deliver.sort((a, b) => compareScheduledDates(a, b, 1));
  groups.past.sort((a, b) => compareScheduledDates(a, b, -1));
  return groups;
}

function compareScheduledDates(a: SessionSummary, b: SessionSummary, direction: 1 | -1) {
  if (a.scheduledStartAt === null) return b.scheduledStartAt === null ? 0 : 1;
  if (b.scheduledStartAt === null) return -1;
  return direction * (Date.parse(a.scheduledStartAt) - Date.parse(b.scheduledStartAt));
}

export function shootClientSummary(shoot: Pick<SessionSummary, 'clients'>): ShootClientSummary {
  return {
    primaryClient: primaryClient(shoot),
    additionalClientCount: Math.max(0, shoot.clients.length - 1),
  };
}

/** Elapsed local calendar days from a scheduled date, not proof the shoot occurred. */
export function calendarDaysSinceScheduled(value: string | null, now: number): number | undefined {
  if (!value) return undefined;
  const scheduled = new Date(value);
  const current = new Date(now);
  if (
    !Number.isFinite(scheduled.getTime()) ||
    !Number.isFinite(current.getTime()) ||
    scheduled.getTime() > now
  ) {
    return undefined;
  }
  // Compare date components on an even-length UTC axis so DST cannot change the day count.
  const localDay = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.max(0, Math.round((localDay(current) - localDay(scheduled)) / 86_400_000));
}
